import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { useLatestEditAreaQuery } from '@/entities/edit-area';
import { Button } from '@/shared/ui';
import { SectionCard } from './section-card';

interface EditAreaSectionProps {
  readonly productId: string;
}

/**
 * 편집영역 상태 카드 — 최신 버전 번호와 정의 여부만 보여준다.
 *
 * 옛 수치 입력 폼(캔버스 크기·인쇄 영역 사각형)은 SUPER-41 계약 교체로 걷어냈다.
 * 컴포넌트·서피스·배치를 사진 위에서 잡는 정의 화면은 에픽 3·4번 작업에서 만든다.
 */
export const EditAreaSection = ({ productId }: EditAreaSectionProps): ReactElement => {
  const navigate = useNavigate();
  const latestQuery = useLatestEditAreaQuery(productId);

  if (latestQuery.isPending) {
    return (
      <SectionCard title="편집 영역">
        <p className="text-muted-foreground">불러오는 중…</p>
      </SectionCard>
    );
  }

  if (latestQuery.isError) {
    return (
      <SectionCard title="편집 영역">
        <p role="alert" className="text-destructive">
          편집 영역을 불러오지 못했습니다: {latestQuery.error.message}
        </p>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="편집 영역"
      description="사진·인쇄 크기·표면·배치를 설정하고 전체 정의를 저장합니다."
    >
      <p data-testid="edit-area-status">
        {latestQuery.data === null
          ? '편집영역 없음 — 인쇄 없는 상품으로 공개됩니다. 인쇄가 있는 상품이면 아래에서 정의하세요.'
          : `편집영역 v${latestQuery.data.latestVersion} 정의됨`}
      </p>
      <Button
        type="button"
        className="mt-3"
        onClick={() => navigate(`/products/editor/${productId}/edit-area`)}
      >
        편집 영역 설정하기
      </Button>
    </SectionCard>
  );
};
